import React, {
  useEffect,
  useState,
  useCallback,
} from 'react';
import {
  Route,
  Switch,
  useHistory,
  useLocation,
  useRouteMatch,
} from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import Loader from '../../../components/Loader';
import {
  Complete,
  DataProviderSelector,
  InveriteServiceProvider,
  FlinksServiceProvider,
  LegacyServiceProviderPlaceholder,
  OpenBankingServiceProviderPlaceholder,
} from './views';
import PreAuthRequestAndRedirect from './PreAuthRequestAndRedirect';
import {
  runInverite,
  runFlinks,
  runSymcor,
} from '../../../services/integrationsService';
import landingPageDisplayOrder from './landing-page-display-order';

import styles from './SmartSelect.container.module.scss';

const maxLandingPageSlots = 8;
const serviceProviderMap = {
  symcor: PreAuthRequestAndRedirect,
  akoya: OpenBankingServiceProviderPlaceholder,
  inverite: InveriteServiceProvider,
  flinks: FlinksServiceProvider,
  legacy: LegacyServiceProviderPlaceholder,
};
const secondaryProviderMapper = {
  inverite: runInverite,
  flinks: runFlinks,
};

function ServiceProviderComponent({
  scoringRequest, dataProvider, skipBankingSurvey, proceedToNext, handleBackButton, webhookData,
}) {
  const [isReady, setIsReady] = useState(false);
  const showBackButton = useCallback(() => {
    setIsReady(true);
  }, []);
  const ServiceProvider = serviceProviderMap[dataProvider.provider] ?? serviceProviderMap.legacy;
  return (
    <>
      <div className={styles.back}>
        <FontAwesomeIcon
          icon="arrow-left"
          onClick={handleBackButton}
          hidden={!isReady}
          aria-hidden="true"
        />
      </div>
      <ServiceProvider
        scoringRequest={scoringRequest}
        dataProvider={dataProvider}
        skipBankingSurvey={skipBankingSurvey}
        proceedToNext={proceedToNext}
        webhookData={webhookData}
        showBackButton={showBackButton}
      />
    </>
  );
}

export default function SmartSelectContainer(props) {
  const {
    region,
    secondary,
    scoringRequest,
    getDataProviderList,
    resumeAuth: { fiId: initialFiId = false } = {},
    nextStep,
  } = props;
  const [dataProviderList, setDataProviderList] = useState(false);
  const [selectedDataProvider, setSelectedDataProvider] = useState(initialFiId);
  const history = useHistory();
  const location = useLocation();
  const { path, url } = useRouteMatch();

  const handleSelection = useCallback((selectedId, selectedProvider) => {
    const foundDataProvider = dataProviderList
      .find((p) => p.selectedProvider.id === selectedId && p.selectedProvider.provider === selectedProvider.provider);
    setSelectedDataProvider(foundDataProvider.selectedProvider);
    history.push(`${url}/provider`, location.state);
  }, [dataProviderList, history, location.state, url]);

  useEffect(() => {
    async function fetchData() {
      const [legacyProviders, symcorProviders, bankMap] = await Promise.all([
        secondaryProviderMapper[secondary.newProvider](
          scoringRequest.requestId,
          scoringRequest.clientId,
          { listProviders: { region } },
        ),
        runSymcor(
          scoringRequest.requestId,
          scoringRequest.clientId,
          { listProviders: true },
        ),
        getDataProviderList(),
      ]);
      setDataProviderList(
        landingPageDisplayOrder(
          maxLandingPageSlots,
          symcorProviders.flat(),
          legacyProviders.shift(),
          bankMap,
          secondary.newProvider,
        ),
      );
    }
    if (!initialFiId) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // this is the browser history not the react router one
  // it is used here to handle routing changes in the iFrames
  const handleBackButton = () => window.history.back();

  if (initialFiId) {
    return (
      <Complete
        handleNextClick={nextStep}
        scoringRequest={scoringRequest}
      />
    );
  }

  if (!dataProviderList) return (<Loader />);

  return (
    <Switch>
      <Route path={`${path}/`} exact>
        <DataProviderSelector
          region="ca"
          dataProviders={dataProviderList}
          handleSelection={handleSelection}
        />
      </Route>
      <Route path={`${path}/provider`}>
        <ServiceProviderComponent
          scoringRequest={scoringRequest}
          dataProvider={selectedDataProvider}
          skipBankingSurvey={props.skipBankingSurvey}
          proceedToNext={props.nextStep}
          webhookData={props.webhookData}
          handleBackButton={handleBackButton}
        />
      </Route>
    </Switch>
  );
}
